/**
 * 基于 Float64Array 的零反序列化四叉树
 *
 * 查询通过数组偏移遍历树结构。
 *
 * 二进制布局（DFS 前序遍历），每个节点:
 * [subtreeSize, minX, minY, maxX, maxY, indicesCount, ...indices, childrenCount, ...children(递归)]
 *
 * subtreeSize: 当前节点（含所有后代）占用的 Float64 元素总数，用于跳过不相交的子树
 */

import { LineSeg2d, pointToSegmentDistance } from '@/editor/math/LineSeg2d';
import type { BoundingBox, LineData, ILineQuadTree } from './types';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { getFootRatio } from '@/editor/math/shape/other';
import { Point2dUtils } from '@/editor/math/Point2dUtils';

// 节点头部字段偏移常量
const OFF_SUBTREE_SIZE = 0;
const OFF_MIN_X = 1;
const OFF_MIN_Y = 2;
const OFF_MAX_X = 3;
const OFF_MAX_Y = 4;
const OFF_INDICES_COUNT = 5;
const HEADER_SIZE = 6; // subtreeSize + bounds(4) + indicesCount

export class BufferLineQuadTree implements ILineQuadTree {
  private data: Float64Array | null;
  private lines: Float32Array;
  private lineCount: number;
  private excludedIndices: Set<number> = new Set();
  /** 预分配的 bitmap，用于遍历时快速去重（避免 Set 开销） */
  private _seen: Uint8Array;

  private constructor(data: Float64Array, lines: Float32Array) {
    this.data = data;
    this.lines = lines;
    this.lineCount = lines.length / 4;
    this._seen = new Uint8Array(this.lineCount);
  }

  static fromBuffer(buffer: ArrayBuffer, lines: Float32Array): BufferLineQuadTree {
    return new BufferLineQuadTree(new Float64Array(buffer), lines);
  }

  // ============ 状态管理 ============

  public setExcludedIndices(excluded: Set<number>): void {
    this.excludedIndices = excluded;
  }

  public clearExclusions(): void {
    this.excludedIndices = new Set();
  }

  public clear(): void {
    this.data = null;
  }

  public getLines(): Float32Array {
    return this.lines;
  }

  public getLineCount(): number {
    return this.lineCount;
  }

  public getLine(index: number): LineData {
    const offset = index * 4;
    return {
      x1: this.lines[offset],
      y1: this.lines[offset + 1],
      x2: this.lines[offset + 2],
      y2: this.lines[offset + 3]
    };
  }

  // ============ 查询方法 ============

  /**
   * 查询与边界框相交的线条索引
   */
  public query(bounds: BoundingBox): number[] {
    const data = this.data;
    if (!data) return [];

    const resultSet = new Set<number>();
    const stack = [0];
    const qMinX = bounds.minX;
    const qMinY = bounds.minY;
    const qMaxX = bounds.maxX;
    const qMaxY = bounds.maxY;

    while (stack.length > 0) {
      const pos = stack.pop() as number;

      // AABB 相交检测
      if (
        qMaxX < data[pos + OFF_MIN_X] ||
        qMinX > data[pos + OFF_MAX_X] ||
        qMaxY < data[pos + OFF_MIN_Y] ||
        qMinY > data[pos + OFF_MAX_Y]
      ) {
        continue;
      }

      // 读取 indices
      const indicesCount = data[pos + OFF_INDICES_COUNT];
      const indicesStart = pos + HEADER_SIZE;
      for (let i = 0; i < indicesCount; i++) {
        resultSet.add(data[indicesStart + i]);
      }

      // 遍历子节点
      const childrenCountPos = indicesStart + indicesCount;
      const childrenCount = data[childrenCountPos];
      let childPos = childrenCountPos + 1;
      for (let i = 0; i < childrenCount; i++) {
        stack.push(childPos);
        childPos += data[childPos + OFF_SUBTREE_SIZE];
      }
    }

    return Array.from(resultSet);
  }

  /**
   * 查询包含指定点的线条索引
   */
  public queryPoint(x: number, y: number): number[] {
    const data = this.data;
    if (!data) return [];

    const resultSet = new Set<number>();
    const stack = [0];

    while (stack.length > 0) {
      const pos = stack.pop() as number;

      // 点包含检测
      if (
        x < data[pos + OFF_MIN_X] ||
        x > data[pos + OFF_MAX_X] ||
        y < data[pos + OFF_MIN_Y] ||
        y > data[pos + OFF_MAX_Y]
      ) {
        continue;
      }

      const indicesCount = data[pos + OFF_INDICES_COUNT];
      const indicesStart = pos + HEADER_SIZE;
      for (let i = 0; i < indicesCount; i++) {
        resultSet.add(data[indicesStart + i]);
      }

      const childrenCountPos = indicesStart + indicesCount;
      const childrenCount = data[childrenCountPos];
      let childPos = childrenCountPos + 1;
      for (let i = 0; i < childrenCount; i++) {
        stack.push(childPos);
        childPos += data[childPos + OFF_SUBTREE_SIZE];
      }
    }

    return Array.from(resultSet);
  }

  /**
   * 查询点附近所有线条索引
   * 内联遍历 + bitmap 去重，避免 Set / Array.from 开销
   */
  public queryPointNearLines(x: number, y: number, radius: number): number[] | null {
    const data = this.data;
    if (!data) return null;

    const qMinX = x - radius;
    const qMinY = y - radius;
    const qMaxX = x + radius;
    const qMaxY = y + radius;
    const excluded = this.excludedIndices;
    const seen = this._seen;
    const result: number[] = [];
    const stack = [0];

    while (stack.length > 0) {
      const pos = stack.pop() as number;

      if (
        qMaxX < data[pos + OFF_MIN_X] ||
        qMinX > data[pos + OFF_MAX_X] ||
        qMaxY < data[pos + OFF_MIN_Y] ||
        qMinY > data[pos + OFF_MAX_Y]
      ) {
        continue;
      }

      const indicesCount = data[pos + OFF_INDICES_COUNT];
      const indicesStart = pos + HEADER_SIZE;
      for (let i = 0; i < indicesCount; i++) {
        const idx = data[indicesStart + i];
        if (seen[idx] === 0 && !excluded.has(idx)) {
          seen[idx] = 1;
          result.push(idx);
        }
      }

      const childrenCountPos = indicesStart + indicesCount;
      const childrenCount = data[childrenCountPos];
      let childPos = childrenCountPos + 1;
      for (let i = 0; i < childrenCount; i++) {
        stack.push(childPos);
        childPos += data[childPos + OFF_SUBTREE_SIZE];
      }
    }

    // 稀疏清零：只重置本次写入的位
    for (let i = 0; i < result.length; i++) {
      seen[result[i]] = 0;
    }

    return result.length > 0 ? result : null;
  }

  /**
   * 查询距离点最近的线条索引
   * 内联遍历 + bitmap 去重，直接计算距离避免中间数组
   */
  public queryPointNearestLine(x: number, y: number, radius: number): number {
    const data = this.data;
    if (!data) return -1;

    const qMinX = x - radius;
    const qMinY = y - radius;
    const qMaxX = x + radius;
    const qMaxY = y + radius;
    const excluded = this.excludedIndices;
    const seen = this._seen;
    const lines = this.lines;
    const collected: number[] = [];
    let minDistance = Infinity;
    let minIndex = -1;
    const stack = [0];

    while (stack.length > 0) {
      const pos = stack.pop() as number;

      if (
        qMaxX < data[pos + OFF_MIN_X] ||
        qMinX > data[pos + OFF_MAX_X] ||
        qMaxY < data[pos + OFF_MIN_Y] ||
        qMinY > data[pos + OFF_MAX_Y]
      ) {
        continue;
      }

      const indicesCount = data[pos + OFF_INDICES_COUNT];
      const indicesStart = pos + HEADER_SIZE;
      for (let i = 0; i < indicesCount; i++) {
        const idx = data[indicesStart + i];
        if (seen[idx] === 0) {
          seen[idx] = 1;
          collected.push(idx);
          if (!excluded.has(idx)) {
            const offset = idx * 4;
            const distance = pointToSegmentDistance(
              x,
              y,
              lines[offset],
              lines[offset + 1],
              lines[offset + 2],
              lines[offset + 3]
            );
            if (distance < minDistance) {
              minDistance = distance;
              minIndex = idx;
            }
          }
        }
      }

      const childrenCountPos = indicesStart + indicesCount;
      const childrenCount = data[childrenCountPos];
      let childPos = childrenCountPos + 1;
      for (let i = 0; i < childrenCount; i++) {
        stack.push(childPos);
        childPos += data[childPos + OFF_SUBTREE_SIZE];
      }
    }

    // 稀疏清零
    for (let i = 0; i < collected.length; i++) {
      seen[collected[i]] = 0;
    }

    return minDistance > radius ? -1 : minIndex;
  }

  /**
   * 查询与圆形区域相交的线条索引
   */
  public queryCircle(centerX: number, centerY: number, radius: number): number[] {
    const candidates = this.query({
      minX: centerX - radius,
      minY: centerY - radius,
      maxX: centerX + radius,
      maxY: centerY + radius
    });
    const result: number[] = [];
    const lines = this.lines;

    for (const index of candidates) {
      const offset = index * 4;
      const x1 = lines[offset];
      const y1 = lines[offset + 1];
      const x2 = lines[offset + 2];
      const y2 = lines[offset + 3];

      const dx = x2 - x1;
      const dy = y2 - y1;
      const length = Math.sqrt(dx * dx + dy * dy);

      let dist: number;
      if (length === 0) {
        dist = Math.sqrt((x1 - centerX) ** 2 + (y1 - centerY) ** 2);
      } else {
        const t = Math.max(0, Math.min(1, ((centerX - x1) * dx + (centerY - y1) * dy) / (length * length)));
        const closestX = x1 + t * dx;
        const closestY = y1 + t * dy;
        dist = Math.sqrt((closestX - centerX) ** 2 + (closestY - centerY) ** 2);
      }

      if (dist <= radius) result.push(index);
    }

    return result;
  }

  /**
   * 根据线段获取最近的平行且投影存在重叠的线段
   */
  public querySegmentNearestParallelLine(startPoint: IPoint2d, endPoint: IPoint2d, maxDistance: number): number {
    const data = this.data;
    if (!data) return -1;

    const { x: sx1, y: sy1 } = startPoint;
    const { x: sx2, y: sy2 } = endPoint;
    const qMinX = Math.min(sx1, sx2) - maxDistance;
    const qMinY = Math.min(sy1, sy2) - maxDistance;
    const qMaxX = Math.max(sx1, sx2) + maxDistance;
    const qMaxY = Math.max(sy1, sy2) + maxDistance;
    const lines = this.lines;

    const excluded = this.excludedIndices;
    const seen = this._seen;
    const collected: number[] = [];
    const stack = [0];
    let minDistance = Infinity;
    let minIndex = -1;

    const referenceLineSeg = new LineSeg2d(startPoint, endPoint);

    while (stack.length > 0) {
      const pos = stack.pop() as number;

      if (
        qMaxX < data[pos + OFF_MIN_X] ||
        qMinX > data[pos + OFF_MAX_X] ||
        qMaxY < data[pos + OFF_MIN_Y] ||
        qMinY > data[pos + OFF_MAX_Y]
      ) {
        continue;
      }

      const indicesCount = data[pos + OFF_INDICES_COUNT];
      const indicesStart = pos + HEADER_SIZE;

      indicesLoop: for (let i = 0; i < indicesCount; i++) {
        const idx = data[indicesStart + i];

        if (seen[idx] === 0) {
          seen[idx] = 1;
          collected.push(idx);

          if (!excluded.has(idx)) {
            const offset = idx * 4;
            const lineStart = { x: lines[offset], y: lines[offset + 1] };
            const lineEnd = { x: lines[offset + 2], y: lines[offset + 3] };
            const isParallel = referenceLineSeg.isParallel(new LineSeg2d(lineStart, lineEnd));

            if (!isParallel) continue indicesLoop;

            const startFootRatio = getFootRatio(referenceLineSeg, lineStart);
            const endFootRatio = getFootRatio(referenceLineSeg, lineEnd);
            if ((startFootRatio < 0 && endFootRatio < 0) || (startFootRatio > 1 && endFootRatio > 1)) {
              // 两线段投影不重叠
              continue indicesLoop;
            }

            const startFootPoint = referenceLineSeg.getPointFromParameter(startFootRatio);
            const dist = Point2dUtils.distanceTo(startFootPoint, lineStart);

            if (dist < minDistance) {
              minDistance = dist;
              minIndex = idx;
            }
          }
        }
      }

      const childrenCountPos = indicesStart + indicesCount;
      const childrenCount = data[childrenCountPos];
      let childPos = childrenCountPos + 1;
      for (let i = 0; i < childrenCount; i++) {
        stack.push(childPos);
        childPos += data[childPos + OFF_SUBTREE_SIZE];
      }
    }

    for (let i = 0; i < collected.length; i++) {
      seen[collected[i]] = 0;
    }

    return minDistance > maxDistance ? -1 : minIndex;
  }
}
